import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { ConfirmDialog, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  FinancialCategoryView,
  ReconcileCandidateView,
  ReconcilerRunResult,
  StatementImportResult,
  StatementLineList,
  StatementLineView,
} from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { LINE_BADGE, LINE_FILTERS, MATCH_KIND_LABEL, confidenceLabel, lineText } from "./labels";

type Filter = (typeof LINE_FILTERS)[number]["value"];

/**
 * Conciliação do extrato (T2.10, RF-FIN-02): importar o OFX do banco (reimportar não duplica), casar cada lançamento
 * com contas em aberto (baixa na data do extrato), com um lançamento já registrado ou criar um novo; ignorar; desfazer.
 * O Conciliador bancário (IA) só sugere: aceitar ou rejeitar é do usuário.
 */
export function ReconcilePanel({ accountId, onChanged }: { accountId: string; onChanged(): void }) {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canWrite = can(perms, "financial_accounts", "create");
  const canAi = canWrite;
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>("pending");
  const [matching, setMatching] = useState<StatementLineView | null>(null);
  const [undoing, setUndoing] = useState<StatementLineView | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const key = ["statement-lines", companyId, accountId];
  const lines = useInfiniteQuery({
    queryKey: [...key, filter],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<StatementLineList>(
        `/bank-statements/${accountId}/lines?${new URLSearchParams({
          status: filter,
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = lines.data?.pages.flatMap((p) => p.data) ?? [];
  const counts = lines.data?.pages[0]?.counts;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    onChanged();
  };
  const post = (path: string, body?: unknown) =>
    api<unknown>(path, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    refresh();
  };
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api<StatementImportResult>(`/bank-statements/${accountId}/import`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: fd,
      });
    },
    onSuccess: (r) => {
      toast(
        "success",
        `Extrato importado: ${r.imported} ${r.imported === 1 ? "lançamento novo" : "lançamentos novos"}` +
          (r.duplicates ? `, ${r.duplicates} já importados` : ""),
      );
      setFilter("pending");
      refresh();
    },
    onError,
    onSettled: () => {
      if (fileRef.current) fileRef.current.value = "";
    },
  });
  const ai = useMutation({
    mutationFn: () =>
      api<ReconcilerRunResult>(`/bank-statements/${accountId}/reconciler`, { method: "POST" }),
    onSuccess: (r) => {
      toast(
        r.created ? "success" : "info",
        r.lines === 0
          ? "Nenhum lançamento pendente com contas ou lançamentos parecidos."
          : `${r.created} ${r.created === 1 ? "sugestão" : "sugestões"} para ${r.lines} ${r.lines === 1 ? "lançamento" : "lançamentos"}. Confira e aceite.`,
      );
      if (r.created) setFilter("suggested");
      refresh();
    },
    onError,
  });
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: "accept" | "reject" }) =>
      api(`/ai/suggestions/${v.id}/${v.decision}`, { method: "POST" }),
    onSuccess: (_, v) => {
      toast(
        "success",
        v.decision === "accept" ? "Sugestão aceita: lançamento conciliado" : "Sugestão rejeitada",
      );
      refresh();
    },
    onError,
  });
  const unmatch = useMutation({
    mutationFn: (id: string) => post(`/bank-statements/lines/${id}/unmatch`),
    onSuccess: () => {
      toast("success", "Conciliação desfeita");
      refresh();
    },
    onError,
  });
  const ignore = useMutation({
    mutationFn: (id: string) => post(`/bank-statements/lines/${id}/match`, { kind: "ignore" }),
    onSuccess: () => {
      toast("success", "Lançamento ignorado");
      refresh();
    },
    onError,
  });

  return (
    <div className="box card mt-3">
      <div className="d-flex flex-wrap align-items-center gap-2 px-3 pt-3 pb-2">
        <h2 className="h6 mb-0 me-auto">Conciliação do extrato (OFX)</h2>
        {canWrite && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".ofx,.OFX,application/x-ofx"
              className="d-none"
              aria-label="Arquivo OFX"
              data-testid="ofx-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={upload.isPending}
              onClick={() => fileRef.current?.click()}
            >
              Importar OFX
            </button>
          </>
        )}
        {canAi && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={ai.isPending}
            onClick={() => ai.mutate()}
          >
            Sugerir com IA
          </button>
        )}
      </div>
      <ul className="nav nav-tabs px-3 mb-2" role="tablist" aria-label="Filtro da conciliação">
        {LINE_FILTERS.map((f) => {
          const n = !counts
            ? null
            : f.value === "pending"
              ? counts.unmatched + counts.suggested
              : f.value === "all"
                ? null
                : counts[f.value];
          return (
            <li key={f.value} className="nav-item" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={filter === f.value}
                className={`nav-link${filter === f.value ? " active" : ""}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
                {n !== null && <> ({n})</>}
              </button>
            </li>
          );
        })}
      </ul>
      <QueryStatus loading={lines.isLoading} error={lines.error} />
      {lines.isSuccess && rows.length === 0 && (
        <p className="text-muted px-3 pb-3 mb-0">
          {filter === "pending"
            ? "Nada pendente. Importe o OFX do banco para conciliar."
            : "Nenhum lançamento."}
        </p>
      )}
      {rows.length > 0 && (
        <div className="table-responsive">
          <table className="table align-middle mb-0" aria-label="Lançamentos do extrato do banco">
            <thead>
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Histórico do banco</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
                <th scope="col">Situação</th>
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const b = LINE_BADGE[l.status];
                const s = l.suggestion;
                return (
                  <tr key={l.id} data-testid="statement-line">
                    <td>{formatDateBR(l.date)}</td>
                    <td className="text-break">
                      {lineText(l)}
                      {s && s.status === "pending" && (
                        <div className="small mt-1">
                          <span className="text-info-emphasis">IA sugere:</span> {s.proposal.label} ·{" "}
                          {formatBRL(s.proposal.amountCents)}
                          {confidenceLabel(s.confidencePct4) && (
                            <> · confiança {confidenceLabel(s.confidencePct4)}</>
                          )}
                          {s.rationale && <div className="text-muted">{s.rationale}</div>}
                        </div>
                      )}
                    </td>
                    <td className={`text-end ${l.amountCents < 0 ? "text-danger" : "text-success"}`}>
                      {formatBRL(l.amountCents)}
                    </td>
                    <td>
                      <StatusBadge tone={b.tone}>{b.label}</StatusBadge>
                      {l.matchKind && l.status === "matched" && (
                        <div className="small text-muted">
                          {MATCH_KIND_LABEL[l.matchKind]}
                          {l.titleIds.length === 1 && (
                            <>
                              {" "}
                              (
                              <Link
                                to={
                                  l.amountCents >= 0
                                    ? "/financas/contas-a-receber/$id"
                                    : "/financas/contas-a-pagar/$id"
                                }
                                params={{ id: l.titleIds[0]! }}
                              >
                                ver conta
                              </Link>
                              )
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-end text-nowrap">
                      {canWrite && s?.status === "pending" && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-success me-1"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ id: s.id, decision: "accept" })}
                          >
                            Aceitar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary me-1"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ id: s.id, decision: "reject" })}
                          >
                            Rejeitar
                          </button>
                        </>
                      )}
                      {canWrite && (l.status === "unmatched" || l.status === "suggested") && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => setMatching(l)}
                          >
                            Conciliar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={ignore.isPending}
                            onClick={() => ignore.mutate(l.id)}
                          >
                            Ignorar
                          </button>
                        </>
                      )}
                      {canWrite && (l.status === "matched" || l.status === "ignored") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={unmatch.isPending}
                          onClick={() => setUndoing(l)}
                        >
                          Desfazer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {lines.hasNextPage && (
        <div className="p-3 text-center">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={lines.isFetchingNextPage}
            onClick={() => lines.fetchNextPage()}
          >
            Carregar mais
          </button>
        </div>
      )}
      {matching && <MatchDialog line={matching} onClose={() => setMatching(null)} onDone={refresh} />}
      <ConfirmDialog
        open={!!undoing}
        title="Desfazer conciliação"
        message={
          undoing?.status === "matched" && !undoing.canUnmatch
            ? "Para desfazer, estorne antes as baixas ou o lançamento criados por esta conciliação (o extrato não apaga dinheiro lançado)."
            : "Desfazer a conciliação? O lançamento do extrato volta para pendente."
        }
        confirmLabel="Desfazer"
        onConfirm={() => {
          if (undoing) unmatch.mutate(undoing.id);
          setUndoing(null);
        }}
        onCancel={() => setUndoing(null)}
      />
    </div>
  );
}

function MatchDialog({
  line,
  onClose,
  onDone,
}: {
  line: StatementLineView;
  onClose(): void;
  onDone(): void;
}) {
  const { companyId, me, api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState<"pick" | "create">("pick");
  const [titles, setTitles] = useState<string[]>([]);
  const [tx, setTx] = useState<string | null>(null);
  const [description, setDescription] = useState(lineText(line).slice(0, 200));
  const [category, setCategory] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const cands = useQuery({
    queryKey: ["statement-candidates", companyId, line.id],
    queryFn: () => api<{ data: ReconcileCandidateView[] }>(`/bank-statements/lines/${line.id}/candidates`),
  });
  const kind = line.amountCents >= 0 ? "revenue" : "expense";
  const categories = useQuery({
    queryKey: ["fin-categories", companyId, kind],
    enabled: mode === "create" && can(me.permissions as Permissions, "financial_categories", "read"),
    queryFn: () => api<{ data: FinancialCategoryView[] }>(`/financial-categories?kind=${kind}`),
  });
  const picked = (cands.data?.data ?? []).filter((c) => c.kind === "title" && titles.includes(c.id));
  const pickedSum = picked.reduce((n, c) => n + c.amountCents, 0);
  const target = Math.abs(line.amountCents);
  const body =
    mode === "create"
      ? { kind: "create", description, financialCategoryId: category }
      : tx
        ? { kind: "transaction", transactionId: tx }
        : { kind: "titles", titleIds: titles };
  const save = useMutation({
    mutationFn: () =>
      api(`/bank-statements/lines/${line.id}/match`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      toast("success", "Lançamento conciliado");
      onDone();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const ready =
    mode === "create" ? description.trim().length > 0 : !!tx || (titles.length > 0 && pickedSum === target);
  return (
    <Modal
      open={open}
      title={`Conciliar ${formatBRL(line.amountCents)} de ${formatDateBR(line.date)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready || save.isPending}
            onClick={() => save.mutate()}
          >
            Conciliar
          </button>
        </>
      }
    >
      <p className="small text-muted mb-2">{lineText(line)}</p>
      <fieldset className="mb-3">
        <legend className="visually-hidden">Como conciliar</legend>
        {(
          [
            ["pick", "Contas em aberto ou lançamento já registrado"],
            ["create", "Criar lançamento novo"],
          ] as const
        ).map(([m, label]) => (
          <div key={m} className="form-check form-check-inline">
            <input
              id={`match-${m}`}
              type="radio"
              name="match-mode"
              className="form-check-input"
              checked={mode === m}
              onChange={() => setMode(m)}
            />
            <label className="form-check-label" htmlFor={`match-${m}`}>
              {label}
            </label>
          </div>
        ))}
      </fieldset>
      {mode === "pick" && (
        <>
          <QueryStatus loading={cands.isLoading} error={cands.error} />
          {cands.isSuccess && cands.data.data.length === 0 && (
            <p className="text-muted">Nenhuma conta em aberto ou lançamento com valor e data parecidos.</p>
          )}
          {cands.isSuccess && cands.data.data.length > 0 && (
            <ul className="list-group mb-2" aria-label="Candidatos">
              {cands.data.data.map((c) => {
                const id = `cand-${c.id}`;
                const checked = c.kind === "title" ? titles.includes(c.id) : tx === c.id;
                return (
                  <li key={c.id} className="list-group-item">
                    <div className="form-check">
                      <input
                        id={id}
                        type={c.kind === "title" ? "checkbox" : "radio"}
                        name={c.kind === "title" ? undefined : "match-tx"}
                        className="form-check-input"
                        checked={checked}
                        onChange={() => {
                          if (c.kind === "title") {
                            setTx(null);
                            setTitles(checked ? titles.filter((x) => x !== c.id) : [...titles, c.id]);
                          } else {
                            setTitles([]);
                            setTx(c.id);
                          }
                        }}
                      />
                      <label className="form-check-label w-100" htmlFor={id}>
                        <span className="d-flex flex-wrap justify-content-between gap-2">
                          <span>
                            {c.kind === "title" ? "Conta" : "Lançamento"}:{" "}
                            {c.contactName ? `${c.contactName} — ` : ""}
                            {c.label}
                          </span>
                          <span>
                            {formatBRL(c.amountCents)} · {formatDateBR(c.date)}
                          </span>
                        </span>
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {titles.length > 0 && (
            <p className={`small ${pickedSum === target ? "text-success" : "text-danger"}`}>
              Soma das contas escolhidas: {formatBRL(pickedSum)} (extrato: {formatBRL(target)})
            </p>
          )}
        </>
      )}
      {mode === "create" && (
        <div className="row">
          <Field label="Histórico" required className="col-12" error={errors.description}>
            <input value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          {categories.data && (
            <Field label="Categoria" className="col-12" error={errors.financialCategoryId}>
              <select
                className="form-select"
                value={category ?? ""}
                onChange={(e) => setCategory(e.target.value || null)}
              >
                <option value="">Sem categoria</option>
                {categories.data.data
                  .filter((c) => c.active)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.path}
                    </option>
                  ))}
              </select>
            </Field>
          )}
        </div>
      )}
    </Modal>
  );
}
